import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import i18next from 'i18next';
import React from 'react';

import { Button, Tooltip } from '@commons/utils/styledLibraryComponents';
import { getTheme } from '@commons/utils/theme';
import SpinnerIcon from '@assets/icons/new-loader.svg';

import { getClientInfo } from '@selectors/client';

import { product as productService } from '@services/product';

import { Container, LoadingIcon } from './styledComponents';

/**
 * Assert that the general form of that page can be saved (product & associated recipes)
 *
 * @param {Object} product - The product to save
 * @param {Object} recipeEatInside - The recipe 'SP' deepsight channel associated to the product
 * @param {Object} recipeTakeAway - The recipe 'LIV' deepsight channel associated to the product
 *
 * @returns {boolean} Whether the form is valid
 */
export function isFormValid(product, composition, selectedCompoType, defaultChannelId) {
  const parsedShelfLife = parseFloat(product.shelfLife);
  const defaultChannelPrice = product.channelPrices?.[defaultChannelId];

  if (
    product.price == null ||
    product.price === '' ||
    defaultChannelPrice?.priceWithTaxes == null ||
    defaultChannelPrice?.priceWithTaxes === '' ||
    !product.name ||
    product.vatRate == null ||
    product.vatRate === '' ||
    defaultChannelPrice?.vatRate == null ||
    defaultChannelPrice?.vatRate === '' ||
    (!!product.shelfLife && (parsedShelfLife <= 0 || !Number.isInteger(parsedShelfLife))) ||
    (selectedCompoType.id !== 'NONE' && isEmpty(composition))
  ) {
    return false;
  }

  return true;
}

/**
 * Handle the save of a given product
 *
 * @param {Object} product                          - The product's data
 * @param {Object} recipes                          - The list of associated recipes
 * @param {Object[]} productCashierProductMappings  - The list of associated product cashier product mappings
 * @param {Object[]} channelPrices                  - The list of channel prices
 * @param {Object} dispatchers                      - Dispatch methods to set loading state of the page while saving
 *
 * @returns {void}
 */
export function saveProduct(product, productCashierProductMappings, channelPrices) {
  return productService
    .updateProduct(product, productCashierProductMappings, channelPrices)
    .then((result) => result)
    .catch(() => {
      throw new Error('La sauvegarde du produit a échoué');
    });
}

/**
 * Handle the save of the entire form (product & its recipes)
 *
 * @param {Props} props - The props linked to the component
 *
 * @returns {void}
 */
export async function handleSave({
  history,
  productId,
  product,
  client: { clientId, defaultChannelId },
  cashierProducts,
  showMessage,
  pageLoading,
  pageLoaded,
  productsCategories,
  productsSubCategories,
}) {
  pageLoading();

  const defaultChannelPrice = product.channelPrices?.[defaultChannelId];

  const categoryNone = productsCategories.find(
    ({ name }) => name === i18next.t('GENERAL.NONE_VALUE'),
  );

  const subCategoryNone = productsSubCategories.find(
    ({ name }) => name === i18next.t('GENERAL.NONE_VALUE'),
  );

  const updatedProduct = {
    ...product,
    ...(!productId || productId === 'create' ? { clientId } : {}),
    priceWithTaxes: defaultChannelPrice?.priceWithTaxes,
    vatRate: defaultChannelPrice?.vatRate,
    shelfLife: product.shelfLife || null,
    categoryId: !product.categoryId ? categoryNone.id : product.categoryId,
    subCategoryId: !product.subCategoryId ? subCategoryNone.id : product.subCategoryId,
  };

  const productCashierProductMappings = cashierProducts.map((cashierProduct) => ({
    productId: product.id,
    cashierProductId: cashierProduct.id,
    priceSync: cashierProduct.priceSync,
  }));

  const formattedChannelPrices = Object.values(product.channelPrices || {}).reduce(
    (acc, channelPrice) => {
      if (
        !channelPrice.channelId ||
        (channelPrice.priceWithTaxes == null && channelPrice.vatRate == null) ||
        (channelPrice.priceWithTaxes === '' && channelPrice.vatRate === '')
      ) {
        return acc;
      }

      acc.push({
        channelId: channelPrice.channelId,
        priceWithTaxes:
          channelPrice.priceWithTaxes != null && channelPrice.priceWithTaxes !== ''
            ? parseFloat(channelPrice.priceWithTaxes)
            : 0,
        vatRate:
          channelPrice.vatRate != null && channelPrice.vatRate !== ''
            ? parseFloat(channelPrice.vatRate)
            : 0,
      });

      return acc;
    },
    [],
  );

  try {
    await saveProduct(updatedProduct, productCashierProductMappings, formattedChannelPrices);

    showMessage(i18next.t('ADMIN.SUPPLIERS.SAVING_SUCCESS'));
    history.goBack();
  } catch (error) {
    showMessage(error.message, 'error');
  } finally {
    pageLoaded();
  }
}

const renderAuditHistoryTrigger = ({ auditButtonLabel, auditButtonLoading, onOpenAuditDrawer }) => {
  if (auditButtonLoading) {
    return <LoadingIcon alt={'component-loader'} height="16px" src={SpinnerIcon} width="16px" />;
  }

  if (!auditButtonLabel) {
    return null;
  }

  const theme = getTheme();

  return (
    <Button
      buttonCustomStyle={{
        paddingLeft: '0px',
        height: '40px',
        font: theme.fonts.textSmallHeight14,
      }}
      color={'inpulse-outline'}
      fontColor={theme.colors.blacks.ipBlack4}
      handleClick={onOpenAuditDrawer}
      icon={'/images/inpulse/history.svg'}
      iconOnLeft={false}
      label={auditButtonLabel}
      noBorder
    />
  );
};

export const ProductDetailsBottomBar = (props) => {
  const {
    product,
    showMessage,
    setOpenEnableModal,
    composition,
    selectedCompoType,
    client: { defaultChannelId },
    auditButtonLabel,
    auditButtonLoading,
    onOpenAuditDrawer,
  } = props;

  const auditHistoryTrigger = renderAuditHistoryTrigger({
    auditButtonLabel,
    auditButtonLoading,
    onOpenAuditDrawer,
  });

  const isEnableButtonDisabled =
    !!product.lnkEntityProductrel && !product.lnkEntityProductrel.active;

  if (!product.active) {
    if (isEnableButtonDisabled) {
      return (
        <Container>
          {auditHistoryTrigger}
          <Tooltip
            displayBigger={true}
            place={'left'}
            renderTooltipTrigger={() => (
              <Button
                buttonCustomStyle={{ margin: '8px 0 0 0' }}
                color={'inpulse-default'}
                handleClick={() => {}}
                icon={'/images/inpulse/power-white-small.svg'}
                label={i18next.t('GENERAL.ACTIVATE')}
                isDisabled
              />
            )}
            text={i18next.t('ADMIN.PRODUCTS.ENABLED_DISABLE_TOOLTIP')}
          />
        </Container>
      );
    }

    return (
      <Container>
        {auditHistoryTrigger}
        <Button
          buttonCustomStyle={{ margin: 'auto' }}
          handleClick={() => setOpenEnableModal(true)}
          icon={'/images/inpulse/power-white-small.svg'}
          label={i18next.t('GENERAL.ACTIVATE')}
        />
      </Container>
    );
  }

  return (
    <Container>
      {auditHistoryTrigger}
      <Button
        buttonCustomStyle={{ margin: 'auto' }}
        handleClick={() =>
          isFormValid(product, composition, selectedCompoType, defaultChannelId)
            ? handleSave({ ...props, showMessage })
            : {}
        }
        icon={'/images/inpulse/save-white-small.svg'}
        isDisabled={!isFormValid(product, composition, selectedCompoType, defaultChannelId)}
        label={i18next.t('GENERAL.SAVE')}
      />
    </Container>
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
});

export default connect(mapStateToProps)(ProductDetailsBottomBar);
